import type { PersonSummary } from '../../services/apiClient'
import type { CVRCompany } from '../../types'
import type { WatchlistItem, WatchlistList } from '../../services/watchlist'
import { listNamesForItem } from '../../services/watchlist'
import { formatStatusLabel, normalizeStatusKey } from '../../utils/format'

export interface Suggestion {
  /** »person:<enhedsnr>« or »cvr:<cvr>« */
  id: string
  name: string
  kind: 'person' | 'company'
  /** What tells this one apart: role and city, or CVR, form and city. */
  meta: string
  /** Country of residence when it is not Denmark, shown as a flag. */
  countryCode: string | null
  /** Names of the watchlist lists it is on; empty when not watched. */
  watchLists: string[]
  watched: boolean
}

const PREPOSITION: Record<NonNullable<PersonSummary['primaryRole']>['role'], string> = {
  Direktør: 'i',
  Ejer: 'af',
  'Reel ejer': 'af',
  Bestyrelsesformand: 'i',
  Bestyrelsesmedlem: 'i',
  Deltager: 'i',
}

/** »Direktør i Nordlys ApS · +2 selskaber · Frederiksberg«. */
export function personMeta(p: PersonSummary): string {
  const parts: string[] = []
  const r = p.primaryRole
  if (r) {
    const role = r.current ? r.role : `Tidl. ${r.role.toLowerCase()}`
    parts.push(`${role} ${PREPOSITION[r.role]} ${r.company}`)
    const others = (p.activeCompanies ?? 0) - (r.current ? 1 : 0)
    if (others > 0) parts.push(`+${others} ${others === 1 ? 'selskab' : 'selskaber'}`)
  }
  if (p.by) parts.push(p.by)
  return parts.join(' · ') || 'Person uden aktive roller'
}

/** »CVR 12345678 · ApS · København Ø«, with the status when it is not active. */
export function companyMeta(c: CVRCompany): string {
  const parts = [`CVR ${c.cvrNummer}`]
  if (c.virksomhedsform) parts.push(c.virksomhedsform)
  if (c.postdistrikt) parts.push(c.postdistrikt)
  const status = c.status ? normalizeStatusKey(c.status) : 'NORMAL'
  if (status !== 'NORMAL' && status !== 'AKTIV') parts.push(formatStatusLabel(c.status))
  return parts.join(' · ')
}

function fold(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/** Every word of the query appears in the name (or the query is the CVR). */
function matchesWatched(item: WatchlistItem, query: string): boolean {
  const q = query.trim()
  if (/^\d{8}$/.test(q)) return item.entityType === 'company' && item.entityId === q
  const name = fold(item.displayName)
  return fold(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => name.includes(w))
}

const MAX_WATCHED = 4

/**
 * How well a name answers the query: the whole name, then the name's
 * start, then every word as a word start, then anywhere. The share of
 * the name the query covers breaks ties, so »lars løkke« ranks »Lars
 * Løkke Rasmussen« above »Lars Løkke Invest ApS«.
 */
export function matchScore(name: string, query: string): number {
  const n = fold(name).replace(/\s+/g, ' ').trim()
  const q = fold(query).replace(/\s+/g, ' ').trim()
  const words = n.split(' ')
  const qWords = q.split(' ').filter(Boolean)
  const level =
    n === q
      ? 4
      : n.startsWith(q)
        ? 3
        : qWords.every((w) => words.some((x) => x.startsWith(w)))
          ? 2
          : qWords.every((w) => n.includes(w))
            ? 1
            : 0
  const covered = words.filter((x) => qWords.some((w) => x.startsWith(w))).length
  return level + covered / Math.max(1, words.length)
}

/**
 * Suggestions for the »Fra« and »Til« pickers. Entities on the watchlist
 * come first — the ones a journalist already follows are usually the
 * ones they mean — then the rest, persons and companies mixed by how
 * well the name matches. Equal matches keep the search's own order.
 */
export function buildSuggestions(
  query: string,
  companies: CVRCompany[],
  persons: PersonSummary[],
  watchItems: WatchlistItem[],
  lists: WatchlistList[]
): Suggestion[] {
  const watchedById = new Map(
    watchItems.map((w) => [
      `${w.entityType === 'company' ? 'cvr' : 'person'}:${w.entityId}`,
      w,
    ])
  )
  const decorate = (
    s: Omit<Suggestion, 'watchLists' | 'watched' | 'countryCode'> & { countryCode?: string | null }
  ): Suggestion => {
    const item = watchedById.get(s.id)
    const country = (s.countryCode ?? item?.countryCode ?? '').toUpperCase()
    return {
      ...s,
      countryCode: country && country !== 'DK' ? country : null,
      watched: !!item,
      watchLists: item ? listNamesForItem(lists, item.listIds) : [],
    }
  }

  const cvr = /^\d{8}$/.test(query.trim())
  const ranked = <T,>(items: T[], name: (t: T) => string) =>
    items.map((t, i) => ({ t, i, score: cvr ? 0 : matchScore(name(t), query) }))
  const hits: Suggestion[] = [
    ...ranked(companies, (c) => c.navn).map(({ t: c, i, score }) => ({
      s: decorate({ id: `cvr:${c.cvrNummer}`, name: c.navn, kind: 'company', meta: companyMeta(c) }),
      i,
      score,
    })),
    ...ranked(
      persons.filter((p) => p.enhedsNummer !== null && p.enhedsNummer !== undefined),
      (p) => p.navn
    ).map(({ t: p, i, score }) => ({
      s: decorate({
        id: `person:${p.enhedsNummer}`,
        name: p.navn,
        kind: 'person',
        meta: personMeta(p),
        countryCode: p.landekode,
      }),
      i,
      score,
    })),
  ]
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((h) => h.s)
  const seen = new Set(hits.map((h) => h.id))

  // Watched entities the search did not return (or ranked below the cut).
  const localOnly = watchItems
    .filter((w) => matchesWatched(w, query))
    .map((w) =>
      decorate({
        id: `${w.entityType === 'company' ? 'cvr' : 'person'}:${w.entityId}`,
        name: w.displayName,
        kind: w.entityType,
        meta: w.entityType === 'company' ? `CVR ${w.entityId}` : 'Person',
      })
    )
    .filter((s) => !seen.has(s.id))

  const watched = [...hits.filter((h) => h.watched), ...localOnly].slice(0, MAX_WATCHED)
  const watchedIds = new Set(watched.map((w) => w.id))
  return [...watched, ...hits.filter((h) => !watchedIds.has(h.id))]
}
